class WorkoutLayerAccessor {
    layer;
    constructor(layer) {
        this.layer = layer;
    }
    getComponentArray(methodName) {
        // If method ends with 's', it's plural, otherwise make it plural
        const pluralName = methodName.endsWith('s') ? methodName : `${methodName}s`;
        return (this.layer?.[pluralName] ||
            []);
    }
    // Filter by property value
    filterComponents(components, property, value) {
        return components.filter((component) => component[property] === value);
    }
    // Filter by function
    filterComponentsByPredicate(components, predicate) {
        return components.filter(predicate);
    }
    // Filter by object properties
    filterComponentsByObject(components, filter) {
        return components.filter((component) => {
            return Object.entries(filter).every(([key, value]) => {
                return component[key] === value;
            });
        });
    }
    handleMethodCall(methodName, propertyOrPredicateOrFilter, valueOrDefault, defaultValue) {
        const components = this.getComponentArray(methodName);
        let filtered;
        let defaultVal;
        // If no arguments provided, return all components
        if (propertyOrPredicateOrFilter === undefined) {
            filtered = components;
            defaultVal = undefined;
        }
        else if (typeof propertyOrPredicateOrFilter === 'function') {
            filtered = this.filterComponentsByPredicate(components, propertyOrPredicateOrFilter);
            defaultVal = valueOrDefault;
        }
        else if (typeof propertyOrPredicateOrFilter === 'object' &&
            propertyOrPredicateOrFilter !== null) {
            // Object-based filtering
            filtered = this.filterComponentsByObject(components, propertyOrPredicateOrFilter);
            defaultVal = valueOrDefault;
        }
        else {
            // Property-based filtering
            filtered = this.filterComponents(components, propertyOrPredicateOrFilter, valueOrDefault);
            defaultVal = defaultValue;
        }
        // If method name doesn't end with 's', return single item
        if (!methodName.endsWith('s')) {
            return filtered[0] ?? defaultVal;
        }
        // Otherwise return array
        return filtered.length > 0 ? filtered : [];
    }
}
/**
 * Creates a dynamic accessor for WorkoutLayer components with type-safe filtering.
 *
 * This function provides a clean API to access and filter any component in a WorkoutLayer
 * using one or many getter methods. It supports property-based, object-based, and
 * function-based typesafe filtering.
 *
 * @param layerContainer - Either a WorkoutLayer directly or an object containing a layer property
 * @returns A proxy object with dynamic methods for accessing layer components
 *
 * @example
 * ```typescript
 * // Property-based filtering
 * const setEdits = Layer(athleteWorkoutLayer).setEdits('workoutExerciseId', 'ex1');
 * const setEdit = Layer(athleteWorkoutLayer).setEdit('workoutExerciseId', 'ex1');
 *
 * // Object-based filtering
 * const setEdits = Layer(athleteWorkoutLayer).setEdits({ workoutExerciseId: 'ex1' });
 * const setEdit = Layer(athleteWorkoutLayer).setEdit({ workoutExerciseId: 'ex1', sortOrder: 1 });
 *
 * // Function-based filtering
 * const setEdits = Layer(athleteWorkoutLayer).setEdits(
 *   (edit) => edit.workoutExerciseId === 'ex1' && edit.sortOrder > 0
 * );
 *
 * // Get all items with no arguments
 * const allSetEdits = Layer(athleteWorkoutLayer).setEdits();
 *
 * // Default values
 * const setEditWithDefault = Layer(athleteWorkoutLayer).setEdit('workoutExerciseId', 'ex1', defaultSetEdit);
 * const setEditWithDefault = Layer(athleteWorkoutLayer).setEdit({ workoutExerciseId: 'ex1' }, defaultSetEdit);
 * ```
 */
export function Layer(layerContainer) {
    const layer = layerContainer
        ? 'layer' in layerContainer
            ? layerContainer.layer
            : layerContainer
        : null;
    const accessor = new WorkoutLayerAccessor(layer);
    return new Proxy(accessor, {
        get(target, prop) {
            if (typeof prop !== 'string') {
                return undefined;
            }
            // Return a function that handles the getter dynamically
            return function (propertyOrPredicateOrFilter, valueOrDefault, defaultValue) {
                return target.handleMethodCall(prop, propertyOrPredicateOrFilter, valueOrDefault, defaultValue);
            };
        },
    });
}
//# sourceMappingURL=layer.js.map